import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import type { CodeoSkill } from '../types';
import { check, repeatsAClass, valuesOf } from '../skillUsage';

export const spotlightFocusCardSkill: CodeoSkill = {
    name: SKILL_NAMES.spotlightFocusCard,
    kind: 'codeo',
    description:
        'Lift one item out of a set — larger, lit, in front — while the rest recede, for picking ' +
        'the recommended plan, the chosen option, or the one that matters out of several.',
    documentUsesSkill: check(
        (script, markup) =>
            repeatsAClass(markup) &&
            valuesOf(script, 'scale').some((value) => value > 1) &&
            valuesOf(script, 'opacity').some((value) => value > 0 && value < 1),
    ),
    instructions: `# Spotlight Focus Card

Several comparable items sit in a row. One of them rises, grows, and takes the light while the others fall back and dim. It is the clearest way to say "this one" when the alternatives have to remain visible for the choice to mean anything.

The recession is doing as much work as the lift. If the others simply stay put, the chosen card reads as bigger; if they visibly step back, it reads as *chosen*.

## Markup

Author the row with the chosen card at the same size as the rest. The hierarchy is created by the tween, not baked into the layout.

\`\`\`html
<div class="absolute inset-0 flex items-center justify-center p-24">
  <div id="plans" class="flex gap-8 items-center" style="width: 1400px">
    <div class="plan flex-1" style="padding: 48px 36px; border-radius: 22px; background: var(--l5-color-1, #1a1e26); font-family: var(--l5-font-0), sans-serif; color: var(--l5-color-0, #ffffff)">
      <div style="font-size: 32px; font-weight: 600; opacity: 0.7">Starter</div>
      <div style="margin-top: 14px; font-size: 72px; font-weight: 800">$0</div>
    </div>
    <div id="plan-chosen" class="plan flex-1" style="padding: 48px 36px; border-radius: 22px; background: var(--l5-color-1, #1a1e26); font-family: var(--l5-font-0), sans-serif; color: var(--l5-color-0, #ffffff)">
      <div style="font-size: 32px; font-weight: 600; opacity: 0.7">Team</div>
      <div style="margin-top: 14px; font-size: 72px; font-weight: 800">$29</div>
    </div>
    <div class="plan flex-1" style="padding: 48px 36px; border-radius: 22px; background: var(--l5-color-1, #1a1e26); font-family: var(--l5-font-0), sans-serif; color: var(--l5-color-0, #ffffff)">
      <div style="font-size: 32px; font-weight: 600; opacity: 0.7">Scale</div>
      <div style="margin-top: 14px; font-size: 72px; font-weight: 800">$99</div>
    </div>
  </div>
</div>
\`\`\`

## Timeline code

\`\`\`js
const tl = gsap.timeline({ paused: true });
const others = gsap.utils.toArray('.plan').filter((el) => el.id !== 'plan-chosen');
const PICK = 1.5;

gsap.set('.plan', { y: 20, opacity: 0 });

tl.to('.plan', { y: 0, opacity: 1, duration: 0.55, ease: 'power3.out', stagger: 0.1 }, 0.3);

// The pick: one comes forward as the others step back.
tl.to('#plan-chosen', { scale: 1.12, y: -18, duration: 0.6, ease: 'power3.out' }, PICK);
// GSAP cannot interpolate into a var(), so resolve the token to a literal colour once
// at build time. Still follows the video's theme; now it actually blends.
const token = (name, fallback) =>
    getComputedStyle(document.documentElement).getPropertyValue(name).trim() || fallback;
const ACCENT = token('--l5-color-2', '#38bdf8');

tl.to('#plan-chosen', { backgroundColor: ACCENT, duration: 0.5, ease: 'power2.out' }, PICK);
tl.to(others, { scale: 0.94, opacity: 0.4, duration: 0.6, ease: 'power2.out' }, PICK + 0.04);

window.__timelines = { main: tl };
\`\`\`

## Adapt

- **Lift amount**: 1.10–1.15 scale is decisive without breaking the row's rhythm. Past 1.25 the chosen card overlaps its neighbours and the layout reads as broken.
- **Recession depth**: 0.4 opacity makes the others context. 0.65 keeps them readable, which matters if the viewer is meant to compare rather than just accept.
- **Light instead of colour**: a shadow bloom and a brightness lift on the chosen card, with no colour change, is quieter and stays on-brand where the palette is tight.
- **Badge**: a small label appearing above the chosen card ("Most popular") names the reason. It must arrive *after* the lift, or it explains a choice that has not happened yet.
- **Sequential consideration**: lifting each card in turn before settling on one reads as deliberation. Costs about a second per card.
- **From a grid**: the same move works out of \`${SKILL_NAMES.cardGridAssemble}\` — assemble the set, then pick from it as a second phase.

## Pitfalls

- **Never tween a colour *into* a \`var()\`.** GSAP interpolates numerically between two resolved colours; handed \`var(--l5-color-2, …)\` as an end value it can only assign it, so the highlight snaps at the end of the tween instead of blending across it. Resolve the token to a literal first, as above — the graphic still follows the theme, because the value is read from the live custom property.
- All cards are \`scale\`- and \`y\`-tweened, so none may carry a CSS \`transform\`. Size them with flex, not with a baked-in scale.
- The chosen card must lift *out* of the row's stacking. Give it a higher \`z-index\` in CSS, or its scaled edges will be overlapped by its neighbours.
- Don't pre-style the winner in the markup. If it starts bigger and brighter, the lift shows nothing and the graphic is a still image with a wiggle.
- The others must recede in the same beat. A lift with no recession reads as a hover state.
- Three or four items. Two is a comparison — use \`${SKILL_NAMES.splitComparisonCards}\`; five is a grid where nothing can be picked legibly.
- Keep the card contents to a label and a figure. A feature list per card is unreadable at video pace.
- After the pick the frame is static — the scene still needs an ambient layer or a slow push.`,
};
